"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SharesManager = void 0;
const interface_1 = require("../../interface");
const apiService_1 = require("../apiService");
/**
 * Provides high-level actions for managing shares.
 *
 * The manager is responsible for handling shares metadata, including
 * API communication, encryption, decryption, and caching.
 *
 * This module uses other modules providing low-level operations, such
 * as API service, cache, crypto service, etc.
 */
class SharesManager {
    logger;
    apiService;
    cache;
    cryptoCache;
    cryptoService;
    account;
    // Cache for My files IDs.
    // Those IDs are required very often, so it is better to keep them in memory.
    // The IDs are not cached in the cache module, as we want to always fetch
    // them from the API, and not from the this.cache.
    myFilesIds;
    rootShares = new Map();
    constructor(logger, apiService, cache, cryptoCache, cryptoService, account) {
        this.logger = logger;
        this.apiService = apiService;
        this.cache = cache;
        this.cryptoCache = cryptoCache;
        this.cryptoService = cryptoService;
        this.account = account;
        this.logger = logger;
        this.apiService = apiService;
        this.cache = cache;
        this.cryptoCache = cryptoCache;
        this.cryptoService = cryptoService;
        this.account = account;
    }
    /**
     * It returns the IDs of the My files section.
     *
     * If the default volume or My files section doesn't exist, it creates it.
     */
    async getMyFilesIDs() {
        if (this.myFilesIds) {
            return this.myFilesIds;
        }
        try {
            const encryptedShare = await this.apiService.getMyFiles();
            // Once any place needs IDs for My files, it will most likely
            // need also the keys for decrypting the tree. It is better to
            // decrypt the share here right away.
            const { share: myFilesShare, key } = await this.cryptoService.decryptRootShare(encryptedShare);
            await this.cryptoCache.setShareKey(myFilesShare.shareId, key);
            await this.cache.setVolume({
                volumeId: myFilesShare.volumeId,
                shareId: myFilesShare.shareId,
                rootNodeId: myFilesShare.rootNodeId,
                creatorEmail: encryptedShare.creatorEmail,
                addressId: encryptedShare.addressId,
            });
            this.myFilesIds = {
                volumeId: myFilesShare.volumeId,
                shareId: myFilesShare.shareId,
                rootNodeId: myFilesShare.rootNodeId,
            };
            return this.myFilesIds;
        }
        catch (error) {
            if (error instanceof apiService_1.NotFoundAPIError) {
                this.logger.warn('Active volume not found, creating a new one');
                return this.createVolume();
            }
            this.logger.error('Failed to get active volume', error);
            throw error;
        }
    }
    /**
     * Creates new default volume for the user.
     *
     * It generates the volume bootstrap, creates the volume on the server,
     * and caches the volume metadata.
     *
     * User can have only one default volume.
     *
     * @throws If the volume cannot be created (e.g., one already exists).
     */
    async createVolume() {
        const address = await this.account.getOwnPrimaryAddress();
        const primaryKey = address.keys[address.primaryKeyIndex];
        const bootstrap = await this.cryptoService.generateVolumeBootstrap(primaryKey.key);
        const myFilesIds = await this.apiService.createVolume({
            addressId: address.addressId,
            addressKeyId: primaryKey.id,
            ...bootstrap.shareKey.encrypted,
        }, {
            ...bootstrap.rootNode.key.encrypted,
            encryptedName: bootstrap.rootNode.encryptedName,
            armoredHashKey: bootstrap.rootNode.armoredHashKey,
        });
        await this.cryptoCache.setShareKey(myFilesIds.shareId, bootstrap.shareKey.decrypted);
        return myFilesIds;
    }
    /**
     * It is a high-level action that retrieves the private key for a share.
     * If prefers to use the cache, but if the key is not there, it fetches
     * the share from the API, decrypts it, and caches it.
     *
     * @param shareId - The ID of the share.
     * @returns The private key for the share.
     * @throws If the share is not found or cannot be decrypted, or cached.
     */
    async getSharePrivateKey(shareId) {
        try {
            const { key } = await this.cryptoCache.getShareKey(shareId);
            return key;
        }
        catch { }
        const encryptedShare = await this.apiService.getRootShare(shareId);
        const { key } = await this.cryptoService.decryptRootShare(encryptedShare);
        await this.cryptoCache.setShareKey(shareId, key);
        return key.key;
    }
    async getMyFilesShareMemberEmailKey() {
        const { volumeId } = await this.getMyFilesIDs();
        try {
            const { addressId } = await this.cache.getVolume(volumeId);
            const address = await this.account.getOwnAddress(addressId);
            return {
                email: address.email,
                addressId,
                addressKey: address.keys[address.primaryKeyIndex].key,
                addressKeyId: address.keys[address.primaryKeyIndex].id,
            };
        }
        catch { }
        const { shareId } = await this.apiService.getVolume(volumeId);
        const share = await this.apiService.getRootShare(shareId);
        await this.cache.setVolume({
            volumeId: share.volumeId,
            shareId: share.shareId,
            rootNodeId: share.rootNodeId,
            creatorEmail: share.creatorEmail,
            addressId: share.addressId,
        });
        const address = await this.account.getOwnAddress(share.addressId);
        return {
            email: address.email,
            addressId: share.addressId,
            addressKey: address.keys[address.primaryKeyIndex].key,
            addressKeyId: address.keys[address.primaryKeyIndex].id,
        };
    }
    async getContextShareMemberEmailKey(shareId) {
        let encryptedShare = this.rootShares.get(shareId);
        if (!encryptedShare) {
            encryptedShare = await this.apiService.getRootShare(shareId);
            this.rootShares.set(shareId, encryptedShare);
        }
        const address = await this.account.getOwnAddress(encryptedShare.addressId);
        return {
            email: address.email,
            addressId: encryptedShare.addressId,
            addressKey: address.keys[address.primaryKeyIndex].key,
            addressKeyId: address.keys[address.primaryKeyIndex].id,
        };
    }
    async isOwnVolume(volumeId) {
        return (await this.getMyFilesIDs()).volumeId === volumeId;
    }
    async getVolumeMetricContext(volumeId) {
        const { volumeId: myVolumeId } = await this.getMyFilesIDs();
        // SDK doesn't support public sharing yet, also public sharing
        // doesn't use a volume but shareURL, thus we can simplify and
        // ignore this case for now.
        if (volumeId === myVolumeId) {
            return interface_1.MetricVolumeType.OwnVolume;
        }
        return interface_1.MetricVolumeType.Shared;
    }
    async loadEncryptedShare(shareId) {
        return this.apiService.getShare(shareId);
    }
}
exports.SharesManager = SharesManager;
//# sourceMappingURL=manager.js.map